import type { AxiosEventBus } from '@shared/axios/axiosEventBus';
import { useLocalStorage } from '@shared/hooks/localStorage';
import { intlOnError } from '@shared/localization/onError';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import qs from 'qs';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { IntlProvider } from 'react-intl';

import createHttpClient from '@/api/client';
import { Localizations } from '@/api/schema/Localizations';
import { useLanguages } from '@/hooks/languages/useLanguages';

import { useAuth } from '../auth';
import { flattenMessages } from './helper';
import type { ILocalizationContext } from './model';
import { translationMessages } from './translations';

export const LANGUAGE_STORAGE_KEY = 'language_i18';

export enum eLanguage {
  enGB = 'en-GB',
  deDE = 'de-DE',
}

export const defaultLanguage = 'en-GB';

// Exported so Storybook can supply a static value: the real provider needs MSAL + /api.
export const LocalizationContext = createContext({} as ILocalizationContext);

export const LocalizationProvider = ({
  children,
  emit,
}: PropsWithChildren<Pick<AxiosEventBus, 'emit'>>) => {
  const { auth, tenant, user } = useAuth();
  const { getLanguageDisplayName } = useLanguages();
  const queryClient = useQueryClient();

  const [storedValue, setValue] = useLocalStorage<eLanguage>(
    LANGUAGE_STORAGE_KEY,
    (user?.cultureName as eLanguage) ?? eLanguage.enGB,
  );

  const localizationsController = useMemo(() => {
    const httpClient = createHttpClient<string>(
      {
        ...(auth && {
          securityWorker: () => ({
            headers: {
              Authorization: `Bearer ${auth.accessToken}`,
            },
          }),
          paramsSerializer: (params: Record<string, unknown>) =>
            qs.stringify(params, { arrayFormat: 'repeat' }),
        }),
      },
      emit,
    );

    return new Localizations(httpClient);
  }, [auth, emit]);

  const [customTranslations, setCustomTranslations] = useState<Record<string, string>>({});

  const getTenantLanguageList = useCallback(() => {
    if (!tenant || !user) return [];
    const { supportedCultureNames } = tenant;

    return supportedCultureNames?.map((cultureName) => {
      const languageName = getLanguageDisplayName(cultureName) ?? '';
      return {
        code: cultureName as eLanguage,
        name: languageName.slice(0, languageName.indexOf('(')),
      };
    });
  }, [getLanguageDisplayName, tenant, user]);

  const languageList = getTenantLanguageList();

  const apiUpdateTranslations = useCallback(async () => {
    await localizationsController.v1LocalizationsFrontendUpdate(
      storedValue,
      Object.entries(customTranslations).map(([key, value]) => ({
        category: 'GENERAL',
        value,
        key,
      })),
    );
  }, [customTranslations, localizationsController, storedValue]);

  const apiGetTranslations = useCallback(
    async () => await localizationsController.v1LocalizationsFrontendDetail(storedValue),
    [localizationsController, storedValue],
  );

  const { mutateAsync: fetchTranslations, isPending: translationsIsLoaded } = useMutation({
    mutationKey: [storedValue],
    mutationFn: apiGetTranslations,
    onSuccess: (res) => {
      setCustomTranslations(
        (res.data as { key: string; value: string }[]).reduce<Record<string, string>>(
          (acc, item) => {
            acc[item.key] = item.value;
            return acc;
          },
          {},
        ),
      );
    },
  });

  const { mutateAsync, isPending } = useMutation({
    mutationKey: [customTranslations],
    mutationFn: apiUpdateTranslations,
  });

  const selectCurrentTranslations = useCallback((locale: eLanguage) => {
    return flattenMessages(translationMessages[locale] ?? translationMessages[eLanguage.enGB]);
  }, []);

  const [translations, setTranslations] = useState<Record<string, string>>(
    selectCurrentTranslations(storedValue),
  );

  useEffect(() => {
    if (!tenant || !user) return;
    void (async () => await fetchTranslations())();
  }, [fetchTranslations, storedValue, tenant, user]);

  useEffect(() => {
    if (user) {
      setTranslations(selectCurrentTranslations(user.cultureName as eLanguage));
      setValue(user.cultureName || eLanguage.enGB);
    }
  }, [user, setValue, selectCurrentTranslations]);

  const handleOnLocaleChange = useCallback(
    (locale: eLanguage) => {
      setTranslations(selectCurrentTranslations(locale));
      setValue(locale);
    },
    [selectCurrentTranslations, setValue],
  );

  useEffect(() => {
    /*
     * All requests have Accept-Language header. The response might be affected by the header
     * and so we need to invalidate all queries.
     */
    void queryClient.invalidateQueries();
  }, [queryClient, storedValue]);

  const saveCustomTranslations = useCallback(async () => {
    //save _tabAdministrationLayout translations to server
    await mutateAsync();
  }, [mutateAsync]);

  const value: ILocalizationContext = useMemo(
    () => ({
      state: {
        languageList,
        locale: storedValue,
        customTranslations,
        translations,
        isPending: translationsIsLoaded || isPending,
      },
      actions: {
        setLocale: handleOnLocaleChange,
        setCustomTranslations,
        saveCustomTranslations,
      },
    }),
    [
      storedValue,
      languageList,
      customTranslations,
      translations,
      translationsIsLoaded,
      isPending,
      handleOnLocaleChange,
      saveCustomTranslations,
    ],
  );

  return (
    <LocalizationContext.Provider value={value}>
      <IntlProvider
        locale={storedValue}
        messages={{ ...translations, ...customTranslations }}
        onError={intlOnError}
      >
        {children}
      </IntlProvider>
    </LocalizationContext.Provider>
  );
};

export const useLocalization = () => useContext(LocalizationContext);
